/**
 * Bob chat for Design Check clients.
 * Streams over SSE from /api/design-check/assistant/message (POST body read
 * via ReadableStream — heartbeats keep CloudFront happy on long generations).
 * Message rendering reuses the portal's presentational ChatMessage component.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { ChatMessage } from '../chatbot';

function AdvisorChat({ jobId }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [toolNote, setToolNote] = useState(null);
  const scrollRef = useRef(null);

  const token = localStorage.getItem('authToken');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/design-check/assistant/messages', {
          headers: { Authorization: `Bearer ${token}` }
        });
        const data = await res.json();
        if (!cancelled && data.success) setMessages(data.messages || []);
      } catch (e) { /* fresh conversation */ }
    })();
    return () => { cancelled = true; };
  }, [token]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput('');
    setBusy(true);
    setToolNote(null);
    setMessages((prev) => [...prev, { role: 'user', content: text, timestamp: new Date().toISOString() }]);

    try {
      const res = await fetch('/api/design-check/assistant/message', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ message: text, jobId })
      });

      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `Request failed (${res.status})`);
      }

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let assistantText = '';

      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const events = buffer.split('\n\n');
        buffer = events.pop();
        for (const evt of events) {
          const line = evt.split('\n').find((l) => l.startsWith('data: '));
          if (!line) continue;
          let payload;
          try { payload = JSON.parse(line.slice(6)); } catch (e) { continue; }
          if (payload.type === 'tool_use') {
            setToolNote('Bob is checking the design guide…');
          } else if (payload.type === 'content') {
            assistantText += payload.content;
          } else if (payload.type === 'error') {
            throw new Error(payload.error);
          }
        }
      }

      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: assistantText || 'Sorry — no answer came back. Please try again.', timestamp: new Date().toISOString() }
      ]);
    } catch (err) {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `⚠️ ${err.message || 'The advisor is unavailable right now.'}`, timestamp: new Date().toISOString() }
      ]);
    } finally {
      setBusy(false);
      setToolNote(null);
    }
  }, [input, busy, token, jobId]);

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="dc-chat">
      <div className="dc-chat-scroll" ref={scrollRef}>
        {messages.length === 0 && (
          <div className="dc-chat-empty">
            Ask Bob anything about your part or about designing for large-format
            pellet printing.<br />
            Try: <em>"Why did my part fail the bridging check?"</em> or{' '}
            <em>"How thick should my walls be?"</em>
          </div>
        )}
        {messages.map((m, i) => (
          <ChatMessage key={m.id || i} message={m} />
        ))}
        {busy && (
          <div className="chatbot-message assistant">
            <div className="chatbot-message-content dc-typing-wrap">
              <span className="dc-typing"><i /><i /><i /></span>
              {toolNote && <span style={{ fontSize: 12, color: 'var(--rf-text-tertiary)' }}>{toolNote}</span>}
            </div>
          </div>
        )}
      </div>
      <div className="dc-chat-form">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={onKeyDown}
          placeholder="Ask Bob about your design…"
          rows={1}
          disabled={busy}
        />
        <button className="dc-btn primary" onClick={send} disabled={busy || !input.trim()}>
          Send
        </button>
      </div>
    </div>
  );
}

export default AdvisorChat;
